/**
 * What needs RM24's hand now — the numbers beside the admin menu (Marti
 * 2026-10-28).
 *
 * A number counts only what RM24 itself must act on, never what waits on a
 * client or a worker: a count that also holds "the client has not approved
 * yet" grows, stays, and teaches the reader to ignore it. It drops by itself
 * when the item is resolved — the next status takes it out.
 *
 * Decision A1 (Marti, docs/analysis/RM24-UX-otsused.md): fresh hours waiting
 * for the client's approval are NOT counted; hours unapproved for more than
 * 3 days are — RM24 then chases the client. That count is grey (K8): a
 * reminder to make, not a fire.
 *
 * `urgent` turns the number red: something is past due or due within a day or
 * two. Otherwise it is grey — to do, not on fire.
 *
 * Pure, so the rules are tested without the shell. Orders reuse
 * `deriveOrderProgress` (PB-14): the menu and the order page must never
 * disagree about what an order needs.
 */
import type { Job, LeaveRequest, Timesheet } from '@rendimees24/shared-types';

import { openEntry } from './liveBoard';
import { groupOrders } from './orderGroups';
import { deriveOrderProgress, type NextItem } from './orderProgress';

export interface ActionCount {
  count: number;
  urgent: boolean;
}

const H = 3_600_000;
const D = 24 * H;
/** An order's staffing is RM24's task this close to its start. */
export const STAFF_HORIZON_MS = 7 * D;
/** "Starts soon": a task on an order this close to its start is urgent. */
export const SOON_MS = 2 * D;
/** A day still open this long after check-in was not ended. */
export const OPEN_DAY_MS = 16 * H;
/**
 * Decision A1: submitted hours older than this (strictly) count as
 * `clientLate`, grey; younger ones are the client's and are not counted.
 */
export const CLIENT_LATE_MS = 3 * D;
/** A question or a client rejection this old is urgent. */
export const STALE_MS = 2 * D;
/** Leave starting this soon is urgent to decide. */
export const LEAVE_SOON_MS = 7 * D;

const ms = (iso: string | undefined) => (iso ? Date.parse(iso) : NaN);
const ACTIONABLE = new Set(['action', 'warn']);
const CLOSED: Job['status'][] = ['cancelled', 'completed', 'invoiced'];

function orderStartMs(jobs: Job[]): number {
  const starts = jobs.map((j) => ms(j.requirements?.startAt)).filter(Number.isFinite);
  return starts.length ? Math.min(...starts) : NaN;
}

/** Worker reports awaiting RM24's decision, per job id (orderReports). */
export type PendingReports = Record<string, number>;

/**
 * The order's RM24 tasks now, with whether any of them is urgent. `reports`
 * are the worker submissions and adjustments awaiting a decision — on a
 * finished trade too, so the caller must pass those trades as well.
 */
export function orderAction(
  jobs: Job[],
  now: string,
  reports?: PendingReports,
): { needs: boolean; urgent: boolean } {
  const nowMs = Date.parse(now);
  const progress = deriveOrderProgress(jobs, { now, pendingReports: reports });
  // Only trades still open: a finished one says nothing about what is due, and
  // the menu reads open trades only — the list must not judge differently.
  const start = orderStartMs(jobs.filter((j) => !CLOSED.includes(j.status)));
  const soon = Number.isFinite(start) && start - nowMs <= SOON_MS;
  const items = [progress.next, ...progress.attention].filter((i: NextItem) => {
    if (!ACTIONABLE.has(i.kind)) return false;
    // Staffing an order weeks away is not today's task.
    if (i.code === 'staff') {
      const first = i.shortages?.[0]?.firstShort?.date;
      const at = first ? Date.parse(`${first}T00:00:00Z`) : start;
      return !Number.isFinite(at) || at - nowMs <= STAFF_HORIZON_MS;
    }
    return true;
  });
  if (!items.length) return { needs: false, urgent: false };
  const urgent = items.some(
    (i) =>
      i.kind === 'warn' ||
      i.code === 'resolveLifecycle' ||
      (soon && i.code !== 'reviewCompletion' && i.code !== 'reviewReports'),
  );
  return { needs: true, urgent };
}

export function ordersAction(jobs: Job[], now: string, reports?: PendingReports): ActionCount {
  let count = 0;
  let urgent = false;
  for (const group of groupOrders(jobs)) {
    const a = orderAction(group.jobs, now, reports);
    if (!a.needs) continue;
    count += 1;
    urgent ||= a.urgent;
  }
  return { count, urgent };
}

export type TimesheetActionReason = 'question' | 'clientRejected' | 'openDay' | 'clientLate' | 'earlyEnd';

/** Why RM24 must act on this day, or null when it waits on nobody at RM24. */
export function timesheetAction(
  ts: Pick<Timesheet, 'status' | 'entries' | 'needsReview' | 'finalRejection' | 'submittedAt'> &
    Partial<Pick<Timesheet, 'shiftEndPending' | 'shiftEnd' | 'invoiceId' | 'draftInvoiceId'>> & {
      rejectedAt?: string;
    },
  now: string,
): { reason: TimesheetActionReason; urgent: boolean } | null {
  const nowMs = Date.parse(now);
  const age = (iso: string | undefined) => nowMs - ms(iso);
  if (ts.needsReview) return { reason: 'question', urgent: age(ts.submittedAt) > STALE_MS };
  if (ts.status === 'rejected' && !ts.finalRejection)
    return { reason: 'clientRejected', urgent: age(ts.rejectedAt ?? ts.submittedAt) > STALE_MS };
  const open = openEntry(ts);
  if (open && age(open.checkInAt) > OPEN_DAY_MS) return { reason: 'openDay', urgent: true };
  // A1: fresh client approvals are left out; over 3 days they count, grey (K8).
  if (ts.status === 'submitted' && age(ts.submittedAt) > CLIENT_LATE_MS)
    return { reason: 'clientLate', urgent: false };
  // M-D4b v4.3 — "Varasem lõpp ootab otsust": RM24 decides who ended the day
  // early; urgent once it has waited past the reminder time.
  if (ts.shiftEndPending && !ts.shiftEnd && !ts.invoiceId && ts.status !== 'invoiced')
    return { reason: 'earlyEnd', urgent: age(ts.submittedAt) > STALE_MS };
  return null;
}

/**
 * The timesheets that need RM24 at `now`, urgent first, then the oldest. Judged
 * against the clock, not only when a document changes: a day turns 16 h open
 * and hours turn 3 days unapproved with no write at all.
 */
export function needingAction<T extends Parameters<typeof timesheetAction>[0]>(
  candidates: T[],
  now: string,
): T[] {
  return candidates
    .map((ts) => ({ ts, a: timesheetAction(ts, now) }))
    .filter((r) => r.a)
    .sort(
      (x, y) =>
        Number(y.a!.urgent) - Number(x.a!.urgent) ||
        (x.ts.submittedAt ?? '').localeCompare(y.ts.submittedAt ?? ''),
    )
    .map((r) => r.ts);
}

export function timesheetsAction(sheets: Parameters<typeof timesheetAction>[0][], now: string): ActionCount {
  let count = 0;
  let urgent = false;
  for (const ts of sheets) {
    const a = timesheetAction(ts, now);
    if (!a) continue;
    count += 1;
    urgent ||= a.urgent;
  }
  return { count, urgent };
}

export function leaveAction(pending: Pick<LeaveRequest, 'from' | 'status'>[], now: string): ActionCount {
  const nowMs = Date.parse(now);
  const live = pending.filter((r) => r.status === 'pending');
  return {
    count: live.length,
    urgent: live.some((r) => Date.parse(`${r.from.slice(0, 10)}T00:00:00Z`) - nowMs <= LEAVE_SOON_MS),
  };
}
